import React, { useState, useEffect } from "react";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { BiPaperPlane, BiMailSend, BiMobileVibration } from "react-icons/bi";
import { MdOutlineMobileFriendly } from "react-icons/md";
import axios from "../../../../axios";
import * as url from "../../../../utils/url";
import { toastContainer, dismissToast } from "../../../../utils/toast";

// "Send a test" callout for the Share Tab. Lets the Admin send a sample of the
// trivvy to THEMSELVES (email / link-via-text / text-only) before sharing it
// operationally — so what they test is exactly what recipients will get. The
// three options + their icons mirror the operational share cards on purpose.
const SendTestCallout = ({ user, survey }) => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  // Live profile fetched on mount (see effect below); null until it resolves.
  const [liveProfile, setLiveProfile] = useState(null);

  const isAdmin =
    user &&
    user.profile &&
    (user.profile.orgAdmin || user.profile.role === "Admin");

  // The Redux profile is cached at login and never re-fetched, so the admin's
  // email/phone can go stale if it's changed mid-session (by them or another
  // admin). Fetch the LIVE profile when the callout mounts and gate the options on
  // it — falling back to the cached profile until the fetch resolves.
  useEffect(() => {
    if (
      user &&
      user.profile &&
      user.profile._id &&
      user.profile.organizationId &&
      user.profile.organizationId._id
    ) {
      axios
        .get(
          url.getUserProfile(
            user.profile._id,
            user.profile.organizationId._id
          )
        )
        .then(({ data }) => {
          if (data && data.data) {
            setLiveProfile(data.data);
          }
        })
        .catch(() => {});
    }
  }, [user]);

  // Prefer the live profile; fall back to the cached one until the fetch resolves.
  let profileSource = null;
  if (liveProfile) {
    profileSource = liveProfile;
  } else if (user && user.profile) {
    profileSource = user.profile;
  }

  // Treat a placeholder/dummy email (phone-only accounts) as "no email", mirroring
  // the BE guard (isDummyEmail). Otherwise the dummy email reads as truthy and the
  // Email option wouldn't disable for an admin who has no real email.
  const adminEmail =
    profileSource && profileSource.email && profileSource.isDummyEmail !== true
      ? profileSource.email
      : null;
  // Require a real-length number (not a short country-code leftover like "+1") so
  // the text options only enable when the admin has an actual phone on file.
  const adminMobile =
    profileSource &&
    profileSource.mobileNumber &&
    profileSource.mobileNumber.length > 6
      ? profileSource.mobileNumber
      : null;

  // A test can only be sent on a published trivvy — the take/link flow only
  // renders published trivvys, so testing a draft would dead-end on a "not
  // published yet" screen. Gate the button and tell the admin to publish first.
  const isPublished = survey && survey.isPublished;

  // Messaging trivvys share only via "Text only" and "Email a link" — there is no
  // "text a link" channel for them, so don't offer it as a test option.
  const isMessaging = survey && survey.isMessaging;

  // Feature is Admin-only (mirrors the rest of the Edit/Share surface).
  if (!isAdmin) {
    return null;
  }

  const doSend = (channel) => {
    setSending(true);
    // Sends a real sample of this trivvy to the signed-in admin only. The BE
    // scopes the send to req.user and writes no operational records.
    axios
      .post(url.sendTrivvyTestToSelf, {
        triviaId: survey._id,
        channel,
      })
      .then(({ data }) => {
        setSending(false);
        const message =
          data && data.data && data.data.message
            ? data.data.message
            : "Test sent.";
        toastContainer(message, "success");
      })
      .catch((err) => {
        setSending(false);
        const message =
          err &&
          err.response &&
          err.response.data &&
          err.response.data.errors &&
          err.response.data.errors.message
            ? err.response.data.errors.message
            : "Could not send the test. Please try again.";
        toastContainer(message, "error");
      });
  };

  const handleSendTest = (channel) => {
    setOpen(false);
    // Email is free — send right away. SMS channels use a credit, so first show
    // the app's in-app confirmation (same toast pattern as the "share the
    // outcome(s)?" confirm) so the admin knows the test will use 1 credit.
    if (channel === "EMAIL") {
      doSend(channel);
      return;
    }
    toastContainer(
      <div className="flex flex-column justify-content-center align-items-center">
        <span
          style={{
            fontWeight: 500,
            fontSize: 16,
            marginBottom: 12,
            textAlign: "center",
          }}
        >
          This test will be sent to your number and will use 1 SMS credit. Send
          it?
        </span>
        <div className="flex justify-content-center align-items-center gap-3 mt-2">
          <button
            className="btn btn-success px-4 py-2 mx-2"
            style={{ borderRadius: 6, fontWeight: 500, fontSize: 15 }}
            onClick={() => {
              dismissToast();
              doSend(channel);
            }}
          >
            Send
          </button>
          <button
            className="btn btn-outline-secondary px-4 py-2 mx-2"
            style={{ borderRadius: 6, fontWeight: 500, fontSize: 15 }}
            onClick={() => dismissToast()}
          >
            Cancel
          </button>
        </div>
      </div>,
      {
        background: "rgb(238 250 236)",
        color: "rgb(0, 83, 0)",
        fontSize: "15px",
        lineHeight: "1.85em",
        borderRadius: "0.5rem",
        boxShadow: "rgb(38, 57, 70) 2px 14px 28px -6px",
      }
    );
  };

  return (
    <div className="send_test_callout">
      <div className="send_test_copy">
        <div className="send_test_title">See it first</div>
        {isPublished ? (
          <div className="send_test_subtitle">
            Send yourself a sample so you see exactly what recipients get —{" "}
            <span className="send_test_only">it goes only to you</span>, nothing is
            shared yet.
          </div>
        ) : (
          <div className="send_test_subtitle">
            <span className="send_test_only">Publish this trivvy first</span> to send
            yourself a test.
          </div>
        )}
      </div>

      <Dropdown
        isOpen={open}
        toggle={() => setOpen(!open)}
        className="send_test_dropdown"
      >
        <DropdownToggle
          className="send_test_btn"
          disabled={sending || !isPublished}
          caret
        >
          <BiPaperPlane className="send_test_btn_ic" /> Send a test
        </DropdownToggle>
        <DropdownMenu right className="send_test_menu">
          <div className="send_test_menu_head">Send a test to yourself</div>

          <DropdownItem
            className="send_test_item"
            onClick={() => handleSendTest("EMAIL")}
            disabled={!adminEmail}
          >
            <span className="send_test_item_ic">
              <BiMailSend />
            </span>
            <span className="send_test_item_text">
              <span className="send_test_item_label">Email a Link</span>
              <span className="send_test_item_dest">To your email</span>
            </span>
          </DropdownItem>

          <DropdownItem
            className="send_test_item"
            onClick={() => handleSendTest("SMS")}
            disabled={!adminMobile}
          >
            <span className="send_test_item_ic">
              <BiMobileVibration />
            </span>
            <span className="send_test_item_text">
              <span className="send_test_item_label">Text ONLY</span>
              <span className="send_test_item_dest">To your phone</span>
            </span>
          </DropdownItem>

          {isMessaging ? null : (
            <DropdownItem
              className="send_test_item"
              onClick={() => handleSendTest("SMS_LINK")}
              disabled={!adminMobile}
            >
              <span className="send_test_item_ic">
                <MdOutlineMobileFriendly />
              </span>
              <span className="send_test_item_text">
                <span className="send_test_item_label">Text a Link</span>
                <span className="send_test_item_dest">To your phone</span>
              </span>
            </DropdownItem>
          )}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default SendTestCallout;
